import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { useDebounce } from "@/hooks/use-debounce";
import { getFormattedDateTime } from "@/utils";
import {
  Search,
  Eye,
  MoreHorizontal,
  Gift,
  Package,
  Plane,
  Hotel,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { PaginationControls } from "@/components/common/PaginationControl";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ProductApi } from "@/api/product.api";
import { BrandApi } from "@/api/brand.api";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { Badge } from "@/components/ui/badge";
import { FlightBookingTableSkeleton } from "@/components/transactions/FlightBookingTableSkeleton";
import { usePlatformCategories } from "@/contexts/PlatformCategoryContext";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import FlightBookingProductCreatePage from "./flight-booking-create";
import ProductTabsNav from "./ProductTabsNav";
import FlightBookingProductGridSkeleton from "./FlightBookingProductGridSkeleton";
import { ProductFilters } from "@/components/common/ProductFilters";

interface FlightBookingProduct {
  id: number;
  createdDate: string;
  brandVO?: {
    brandName?: string;
    brandImageUri?: string;
  };
  redemptionMerchantVO?: {
    merchantName?: string;
    countryVO?: {
      name?: string;
    };
  };
  productCode: string;
  productName: string;
  productImageUri?: string;
  platformCategoryIds?: number[];
  productPrice?: number;
  currencyVO?: {
    iso3CurrencyCode?: string;
    currencySymbol?: string;
  };
}

interface FlightBookingResponse {
  giftCardProductVOs: FlightBookingProduct[];
  totalRecords: number;
}

interface Column {
  header: string;
  accessorKey?: keyof FlightBookingProduct;
  id?: string;
  cell?: (row: FlightBookingProduct) => React.ReactNode;
}

const FlightBookingPage = () => {
  const { refreshToken } = useAuth();
  const [products, setProducts] = useState<FlightBookingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { getCategoryNames } = usePlatformCategories();

  // Filter states
  const [filters, setFilters] = useState<{
    redemptionMerchantId?: number;
    brandId?: number;
    platformCategoryId?: number;
  }>({});
  const [searchType, setSearchType] = useState("productCode");
  const [searchTerm, setSearchTerm] = useState("");
  const [brands, setBrands] = useState([]);
  const [merchants, setMerchants] = useState([]);

  const getFileUrl = (uri) => {
    if (!uri) return "/placeholder.svg";
    if (uri.startsWith("http")) return uri;
    if (uri.startsWith("/")) {
      return `${import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL}${uri}`;
    }
    return `${
      import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
    }/file/download?fileUri=${encodeURIComponent(uri)}`;
  };

  const handleApiError = async (response: Response) => {
    if (response.status === 401 || response.status === 403) {
      const refreshSuccess = await refreshToken();
      if (refreshSuccess) {
        return true;
      }
      throw new Error("Authentication failed");
    }
    return false;
  };

  const fetchFlightBookings = async (
    overrideFilters = filters,
    overrideSearchType = searchType,
    overrideSearchTerm = searchTerm
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Build search payload based on search type
      let productName = null;
      let productCode = null;

      if (overrideSearchTerm && overrideSearchType === "productCode") {
        productCode = overrideSearchTerm;
      } else if (overrideSearchTerm && overrideSearchType === "productName") {
        productName = overrideSearchTerm;
      }

      const payload = {
        productName,
        productCode,
        redemptionMerchantId: overrideFilters.redemptionMerchantId || null,
        brandId: overrideFilters.brandId || null,
        countryId: null,
        platformCategoryId: overrideFilters.platformCategoryId || null,
        productType: "FLIGHT_BOOKING",
        pageNumber: currentPage,
        pageSize,
        sortOrder,
        sortBy,
      };

      const { data } = await ProductApi.fetchFlightBookingProducts(payload);
      setProducts(data.giftCardProductVOs || []);
      setTotalProducts(data.totalRecords || 0);
    } catch (error) {
      setError(error?.message || "Failed to fetch flight bookings");
      toast.error(error?.message || "Failed to fetch flight bookings");
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const { data } = await BrandApi.fetchAllBrands();
      setBrands(data || []);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const fetchMerchants = async () => {
    try {
      const res = await fetch(
        `${
          import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
        }/redemptionMerchant/getRedemptionMerchants`,
        {
          method: "POST",
          headers: {
            accept: "application/json, text/plain, */*",
            "content-type": "application/json",
          },
          body: JSON.stringify({
            merchantName: null,
            merchantCode: null,
            warehouseCountryId: null,
            pageNumber: 0,
            pageSize: 100,
            sortOrder: "desc",
            sortBy: "createdDate",
          }),
          credentials: "include",
        }
      );
      const data = await res.json();
      const merchantsArray = data.redemptionMerchantVOS || [];
      setMerchants(merchantsArray);
    } catch (error) {
      console.error("Error fetching merchants:", error);
      setMerchants([]);
    }
  };

  useEffect(() => {
    fetchBrands();
    fetchMerchants();
  }, []);

  useEffect(() => {
    fetchFlightBookings();
  }, [currentPage, pageSize, searchTerm, searchType, sortBy, sortOrder]);

  useEffect(() => {
    fetchFlightBookings();
  }, [filters]);

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(0);
    fetchFlightBookings(newFilters, searchType, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setSearchType("productCode");
    setFilters({});
    setCurrentPage(0);
    fetchFlightBookings({}, "productCode", "");
  };

  const columns: Column[] = [
    {
      header: "Create Date",
      accessorKey: "createdDate",
      cell: (row) => (
        <div>
          {row.createdDate
            ? getFormattedDateTime(row.createdDate) || "-"
            : "-"}
        </div>
      ),
    },
    {
      header: "Logo",
      cell: (row) => (
        <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
          <img
            src={getFileUrl(row.productImageUri || row.brandVO?.brandImageUri)}
            alt={row.productName}
            className="max-h-8 max-w-8 object-contain"
            onError={(e) => {
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
        </div>
      ),
    },

    {
      header: "Merchant",
      cell: (row) => <div>{row.redemptionMerchantVO?.merchantName || "-"}</div>,
    },
    {
      header: "Product Code",
      accessorKey: "productCode",
      cell: (row) => (
        <div className="font-mono text-sm">{row.productCode || "-"}</div>
      ),
    },
    {
      header: "Product Name",
      accessorKey: "productName",
      cell: (row) => (
        <div className="font-medium">{row.productName || "-"}</div>
      ),
    },
    {
      header: "Country",
      cell: (row) => (
        <div>{row.redemptionMerchantVO?.countryVO?.name || "-"}</div>
      ),
    },
    {
      header: "Platform Category",
      cell: (row) => (
        <div>
          {row.platformCategoryIds && row.platformCategoryIds.length > 0 ? (
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              {getCategoryNames(row.platformCategoryIds).join(", ") ||
                `Category ${row.platformCategoryIds[0]}`}
            </Badge>
          ) : (
            "-"
          )}
        </div>
      ),
    },

    {
      header: "Actions",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/rewardpoint/products/flight-booking-view/${row.id}`}>
                View Details
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Products</h1>
          <p className="text-gray-500">Manage different types of products</p>
        </div>
        <Link to="/rewardpoint/products/flight-booking/create">
          <Button>
            <Plus className="mr-2 h-4 w-4" /> Add Product
          </Button>
        </Link>
      </div>

      {/* Product Type Navigation */}
      <ProductTabsNav />

      {/* Flight Booking Content */}
      <div className="mt-6">
        {/* Filters */}
        <ProductFilters
          onFilterChange={handleFilterChange}
          onReset={handleReset}
          brands={brands}
          merchants={merchants}
          searchType={searchType}
          setSearchType={setSearchType}
          searchTerm={searchTerm}
          onSearch={setSearchTerm}
        />

        {/* Table */}
        {loading ? (
          <FlightBookingProductGridSkeleton />
        ) : (
          <DataTable
            title="All Flight Bookings"
            data={products}
            columns={columns}
            customTableBody={undefined}
          />
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Rows per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setCurrentPage(0);
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder={pageSize.toString()} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <PaginationControls
            totalPages={Math.ceil(totalProducts / pageSize)}
            currentPage={currentPage}
            onNextHandler={() =>
              setCurrentPage((p) =>
                Math.min(Math.ceil(totalProducts / pageSize) - 1, p + 1)
              )
            }
            onPrevHandler={() => setCurrentPage((p) => Math.max(0, p - 1))}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </div>
  );
};

export default FlightBookingPage;
